import React from 'react';
import LineChartBasic from './LineChartBasic';

/**
 * Component to display electricity rates or solar generation profiles
 * @param {Object} props
 * @param {Object|Array} props.data - The data to display
 * @param {string} props.type - Type of data ('electricity_buy_rates', 'electricity_sell_rates', or 'solar_generation_profile')
 */
const ChartViewer = ({ data, type }) => {
  // If data is a string (JSON), try to parse it
  let parsedData;
  try {
    parsedData = typeof data === 'string' ? JSON.parse(data) : data;
  } catch (e) {
    return <div className="chart-error">Invalid data format</div>;
  }
  
  // If no data or empty data
  if (!parsedData || (Array.isArray(parsedData) && parsedData.length === 0) || 
      (typeof parsedData === 'object' && Object.keys(parsedData).length === 0)) {
    return <div className="chart-empty">No data available</div>;
  }

  // Process solar generation profile data to convert from 15-minute intervals to hourly averages
  if (type === 'solar_generation_profile' && Object.keys(parsedData).length === 96) {
    // Create a new object for hourly data
    const hourlyData = {};
    
    // Process each hour (group of 4 15-minute intervals)
    for (let hour = 0; hour < 24; hour++) {
      // Calculate indices for this hour's 15-minute intervals
      const startIdx = hour * 4;
      
      // Calculate average of the 4 values for this hour
      let hourSum = 0;
      for (let i = 0; i < 4; i++) {
        hourSum += parseFloat(parsedData[String(startIdx + i)] || 0);
      }
      
      // Store the average in the hourly data object
      hourlyData[hour] = hourSum / 4;
    }
    
    // Replace the original data with hourly averages
    parsedData = hourlyData;
  }

  // Configure chart based on data type
  let chartConfig = {
    title: 'Data',
    xLabel: 'Hour',
    yLabel: 'Value',
    lineColor: '#5E5ADE'
  };

  switch (type) {
    case 'electricity_buy_rates':
      chartConfig = {
        title: 'Electricity Buy Rates',
        xLabel: 'Hour of Day',
        yLabel: 'Rate ($/kWh)',
        lineColor: '#E74C3C' // Red
      };
      break;
    case 'electricity_sell_rates':
      chartConfig = {
        title: 'Electricity Sell Rates',
        xLabel: 'Hour of Day',
        yLabel: 'Rate ($/kWh)',
        lineColor: '#27AE60' // Green
      };
      break;
    case 'solar_generation_profile':
      chartConfig = {
        title: 'Solar Generation Profile',
        xLabel: 'Hour of Day',
        yLabel: 'Generation Ratio',
        lineColor: '#F39C12', // Orange/Yellow
        formatXAxis: true // Enable time formatting for solar profile
      };
      break;
    default:
      break;
  }

  return (
    <div className="chart-container">
      <LineChartBasic 
        data={parsedData} 
        title={chartConfig.title}
        xLabel={chartConfig.xLabel}
        yLabel={chartConfig.yLabel}
        lineColor={chartConfig.lineColor}
        formatXAxis={chartConfig.formatXAxis}
      />
    </div>
  );
};

export default ChartViewer; 